import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, User } from "lucide-react";
import { PHONE, PHONE_HREF, BOOKING_URL } from "@/data/site";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LINKS = [
  { to: "/services", label: "Services" },
  { to: "/maintenance-plans", label: "Maintenance Plans" },
  { to: "/commercial", label: "Commercial" },
  { to: "/condo-associations", label: "Condo Programs" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/about", label: "About" },
  { to: "/faqs", label: "FAQs" },
];

const TopBar = () => (
  <div className="bg-brand-ink text-white text-xs sm:text-sm">
    <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
      <span data-testid="topbar-trust" className="hidden sm:block tracking-wide">Serving South Florida Since 1998 · Licensed &amp; Insured Contractors · Estimate, Finance &amp; Book 100% Online</span>
      <a data-testid="topbar-call-link" href={PHONE_HREF} className="flex items-center gap-2 font-medium hover:text-brand-ice transition-colors duration-200">
        <Phone className="w-3.5 h-3.5" /> {PHONE}
      </a>
    </div>
  </div>
);

const MobileMenu = ({ open, setOpen, user }) => (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetTrigger asChild>
      <button data-testid="nav-mobile-menu-btn" className="lg:hidden p-2 text-brand-ink" aria-label="Menu">
        <Menu className="w-6 h-6" />
      </button>
    </SheetTrigger>
    <SheetContent side="right" className="w-72">
      <div className="flex flex-col gap-1 mt-8">
        {LINKS.map((l) => (
          <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="px-3 py-3 text-base font-medium text-brand-ink hover:bg-brand-subtle rounded-sm transition-colors duration-200">
            {l.label}
          </Link>
        ))}
        <Link to={user ? "/account" : "/login"} onClick={() => setOpen(false)} data-testid="mobile-nav-account"
          className="px-3 py-3 text-base font-medium text-brand hover:bg-brand-subtle rounded-sm transition-colors duration-200">
          {user ? "Manage My AC" : "Sign In"}
        </Link>
      </div>
    </SheetContent>
  </Sheet>
);

export const Navbar = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50">
      <TopBar />
      <nav className="bg-white/80 backdrop-blur-xl border-b border-zinc-200/50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <img src="/logo-horizontal.webp" alt="Central Air Depot — AC Service, Install, Maintenance" className="h-10 sm:h-12 w-auto" />
          </Link>
          <div className="hidden lg:flex items-center gap-6">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `text-sm font-medium transition-colors duration-200 ${isActive ? "text-brand" : "text-zinc-600 hover:text-brand-ink"}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Link to={user ? "/account" : "/login"} data-testid="nav-account-link"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-brand px-3 py-2 transition-colors duration-200">
              <User className="w-4 h-4" /> {user ? "Manage My AC" : "Sign In"}
            </Link>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-book-service-btn"
              className="bg-brand hover:bg-brand-dark text-white text-sm font-bold px-4 py-2.5 rounded-sm transition-colors duration-200">
              Book Service
            </a>
            <MobileMenu open={open} setOpen={setOpen} user={user} />
          </div>
        </div>
      </nav>
    </header>
  );
};
